import apiClient from '../apiClient';
import { webLog } from '../grpc/webLogger';
import { useRevitParametersStore } from '../../stores/useRevitParametersStore';
import { DEFAULT_PIPEEX_PROFILE_NAME } from '../../config/defaultPipeexProfile/defaultPipeexProfile';
import {
  buildDefaultPipeexSettingsDto,
  buildPcfSettingsDtoFromStore,
} from './pcfSettingsDtoBuilder';
import {
  logPcfSettingsDebug,
  summarizePcfSettingsDtoDataFlowForDebug,
  summarizePcfSettingsDtoForDebug,
  snapshotCurrentPcfSettingsStoreForDebug,
} from './pcfSettingsDebug';
import {
  extractApiErrorDebugDetails,
  getProfileKey,
  getSettingsTypeWireVariants,
  deduplicateSettingsProfiles,
  isAuthFailureError,
  isRetryableSettingsTypeContractError,
  normalizeScopedProfileName,
  normalizeSettingsType,
  shouldFallbackFromPostToPut,
  shouldFallbackFromPutToPost,
} from './pcfSettingsSerialization';
import {
  SettingsType,
  type CreatePcfSettingsDto,
  type PcfSettingsDto,
  type SaveCurrentConfigurationTraceResult,
  type SettingInfoDto,
} from './pcfSettingsTypes';
import {
  errorPcfSettingsRemote,
  summarizeProfileBoltPayloadForDebug,
  warnPcfSettingsRemote,
} from './pcfSettingsRemoteDebug';

function debugPcfSettings(message: string, payload?: unknown): void {
  logPcfSettingsDebug('pcfSettingsClient', message, payload);
}

export async function persistCurrentConfigurationToPipeexDetailed(
  name = 'PipeEXUI Design',
  settingsType: SettingsType = SettingsType.User,
  sourcePayloadOverride?: PcfSettingsDto
): Promise<SaveCurrentConfigurationTraceResult> {
  const normalizedSettingsType = normalizeSettingsType(settingsType);
  const normalizedName = normalizeScopedProfileName(name, normalizedSettingsType, DEFAULT_PIPEEX_PROFILE_NAME);
  const revitParametersState = useRevitParametersStore.getState();
  if (!sourcePayloadOverride && !revitParametersState.isLoaded && !revitParametersState.isLoading) {
    try {
      await revitParametersState.fetchParameters();
    } catch {
      // Parameter discovery is best-effort; profile save still proceeds.
    }
  }
  const sourcePayload = sourcePayloadOverride ?? buildPcfSettingsDtoFromStore(normalizedSettingsType);
  const summary = summarizePcfSettingsDtoForDebug(sourcePayload);
  const dataFlow = summarizePcfSettingsDtoDataFlowForDebug(sourcePayload);
  const storeSnapshot = snapshotCurrentPcfSettingsStoreForDebug();
  const profileKey = getProfileKey(normalizedName, normalizedSettingsType);
  webLog.info(
    'pcfSettingsClient',
    `saveCurrentConfigurationToPipeex: start name=${normalizedName} settingsType=${normalizedSettingsType} summary=${JSON.stringify(summary)}`
  );
  debugPcfSettings('[pcfSettingsClient] saveCurrentConfigurationToPipeex payload detail', {
    profileKey,
    profileName: normalizedName,
    settingsType: normalizedSettingsType,
    dataFlow,
  });
  debugPcfSettings('[pcfSettingsClient] saveCurrentConfigurationToPipeex bolt payload detail', {
    profileKey,
    profileName: normalizedName,
    settingsType: normalizedSettingsType,
    storeBolt: storeSnapshot.bolt,
    payloadBolt: summarizeProfileBoltPayloadForDebug(dataFlow),
  });

  let lastError: unknown = null;
  const wireVariants = getSettingsTypeWireVariants(normalizedSettingsType);
  for (let i = 0; i < wireVariants.length; i += 1) {
    const wireType = wireVariants[i];
    const payload: CreatePcfSettingsDto = {
      ...sourcePayload,
      name: normalizedName,
      settingsType: wireType,
    };

    const attemptTrace = {
      profileKey: getProfileKey(normalizedName, normalizedSettingsType),
      profileName: normalizedName,
      settingsType: normalizedSettingsType,
      wireType,
      attempt: i + 1,
      totalAttempts: wireVariants.length,
      summary,
    };

    try {
      await apiClient.post('/pcf_settings', payload, { timeout: 10000 });
      webLog.info(
        'pcfSettingsClient',
        `saveCurrentConfigurationToPipeex: remote save succeeded via POST name=${normalizedName} settingsType=${normalizedSettingsType}`
      );
      debugPcfSettings('[pcfSettingsClient] saveCurrentConfigurationToPipeex API POST upsert success detail', {
        profileKey: getProfileKey(normalizedName, normalizedSettingsType),
        wireType,
        summary,
      });
      return {
        savedVia: 'api',
        profileName: normalizedName,
        settingsType: normalizedSettingsType,
        summary,
      };
    } catch (error) {
      const postError = extractApiErrorDebugDetails(error);
      const shouldFallback = shouldFallbackFromPostToPut(error);
      const postFailureTrace = {
        ...attemptTrace,
        error: postError,
      };
      debugPcfSettings('[pcfSettingsClient] saveCurrentConfigurationToPipeex API POST failure detail', {
        ...postFailureTrace,
        shouldFallbackToPut: shouldFallback,
      });

      if (shouldFallback) {
        warnPcfSettingsRemote(
          '[pcfSettingsClient] saveCurrentConfigurationToPipeex POST rejected; falling back to PUT',
          postFailureTrace
        );
        try {
          await apiClient.put('/pcf_settings', payload, { timeout: 10000 });
          webLog.info(
            'pcfSettingsClient',
            `saveCurrentConfigurationToPipeex: remote save succeeded via PUT fallback name=${normalizedName} settingsType=${normalizedSettingsType}`
          );
          debugPcfSettings('[pcfSettingsClient] saveCurrentConfigurationToPipeex API PUT fallback success detail', {
            profileKey: getProfileKey(normalizedName, normalizedSettingsType),
            wireType,
            summary,
          });
          warnPcfSettingsRemote(
            '[pcfSettingsClient] saveCurrentConfigurationToPipeex PUT fallback succeeded',
            {
              profileKey: getProfileKey(normalizedName, normalizedSettingsType),
              profileName: normalizedName,
              settingsType: normalizedSettingsType,
              wireType,
              savedVia: 'api',
              fallbackFrom: postError,
            }
          );
          return {
            savedVia: 'api',
            profileName: normalizedName,
            settingsType: normalizedSettingsType,
            summary,
          };
        } catch (putError) {
          lastError = putError;
          const putErrorDetails = extractApiErrorDebugDetails(putError);
          const willRetryPut =
            i < wireVariants.length - 1 && isRetryableSettingsTypeContractError(putError);
          const putFailureTrace = {
            ...attemptTrace,
            fallbackFrom: postError,
            putError: putErrorDetails,
            willRetryWithAlternateSettingsType: willRetryPut,
          };
          if (willRetryPut) {
            warnPcfSettingsRemote(
              '[pcfSettingsClient] saveCurrentConfigurationToPipeex PUT fallback failed; retrying alternate settingsType wire value',
              putFailureTrace
            );
            continue;
          }

          errorPcfSettingsRemote(
            '[pcfSettingsClient] saveCurrentConfigurationToPipeex PUT fallback failed',
            putFailureTrace
          );
          break;
        }
      }

      lastError = error;
      const willRetryPut =
        i < wireVariants.length - 1 && isRetryableSettingsTypeContractError(error);
      if (willRetryPut) {
        warnPcfSettingsRemote(
          '[pcfSettingsClient] saveCurrentConfigurationToPipeex POST failed; retrying alternate settingsType wire value',
          {
            ...postFailureTrace,
            willRetryWithAlternateSettingsType: true,
          }
        );
        continue;
      }

      errorPcfSettingsRemote(
        '[pcfSettingsClient] saveCurrentConfigurationToPipeex POST failed without PUT fallback',
        {
          ...postFailureTrace,
          willRetryWithAlternateSettingsType: false,
        }
      );
      break;
    }
  }

  if (isAuthFailureError(lastError)) {
    throw lastError ?? new Error('Authentication failed while saving settings profile');
  }

  throw lastError ?? new Error('Failed to save settings profile');
}

export async function saveCurrentConfigurationToPipeex(
  name = 'PipeEXUI Design',
  settingsType: SettingsType = SettingsType.User,
  sourcePayloadOverride?: PcfSettingsDto
): Promise<void> {
  await persistCurrentConfigurationToPipeexDetailed(name, settingsType, sourcePayloadOverride);
}

export async function saveDefaultPipeexProfile(
  settingsType: SettingsType = SettingsType.User
): Promise<void> {
  const normalizedSettingsType = normalizeSettingsType(settingsType);
  const sourcePayload = buildDefaultPipeexSettingsDto(normalizedSettingsType);

  let lastError: unknown = null;
  const wireVariants = getSettingsTypeWireVariants(normalizedSettingsType);
  for (let i = 0; i < wireVariants.length; i += 1) {
    const wireType = wireVariants[i];
    const payload: CreatePcfSettingsDto = {
      ...sourcePayload,
      name: DEFAULT_PIPEEX_PROFILE_NAME,
      settingsType: wireType,
    };

    try {
      await apiClient.put('/pcf_settings', payload, { timeout: 10000 });
      return;
    } catch (error) {
      if (shouldFallbackFromPutToPost(error)) {
        try {
          await apiClient.post('/pcf_settings', payload, { timeout: 10000 });
          return;
        } catch (postError) {
          lastError = postError;
          if (i < wireVariants.length - 1 && isRetryableSettingsTypeContractError(postError)) {
            continue;
          }
          break;
        }
      }

      lastError = error;
      if (i < wireVariants.length - 1 && isRetryableSettingsTypeContractError(error)) {
        continue;
      }
      break;
    }
  }

  if (isAuthFailureError(lastError)) {
    throw lastError ?? new Error('Authentication failed while seeding default settings profile');
  }

  throw lastError ?? new Error('Failed to seed default settings profile');
}

export async function getPipeexSettingsProfiles(): Promise<SettingInfoDto[]> {
  debugPcfSettings('[pcfSettingsClient] getPipeexSettingsProfiles start', {
    apiBase: '/pcf_settings/All',
  });

  const response = await apiClient.get<SettingInfoDto[]>('/pcf_settings/All', {
    timeout: 10000,
  });
  const rows = response.data || [];
  const apiProfiles = deduplicateSettingsProfiles(rows.map((row) => ({
    ...row,
    settingsType: normalizeSettingsType((row as { settingsType?: unknown }).settingsType),
    name: normalizeScopedProfileName(
      row.name,
      normalizeSettingsType((row as { settingsType?: unknown }).settingsType),
      DEFAULT_PIPEEX_PROFILE_NAME
    ),
  })));
  debugPcfSettings('[pcfSettingsClient] getPipeexSettingsProfiles API success', {
    apiProfileCount: apiProfiles.length,
  });
  return apiProfiles;
}

export async function getPipeexSettings(
  name: string,
  settingsType: SettingsType
): Promise<PcfSettingsDto> {
  const normalizedSettingsType = normalizeSettingsType(settingsType);
  const normalizedName = normalizeScopedProfileName(name, normalizedSettingsType, DEFAULT_PIPEEX_PROFILE_NAME);
  debugPcfSettings('[pcfSettingsClient] getPipeexSettings start', {
    profileKey: getProfileKey(normalizedName, normalizedSettingsType),
    apiBase: '/pcf_settings',
  });

  let lastError: unknown = null;
  const wireVariants = getSettingsTypeWireVariants(normalizedSettingsType);
  for (let i = 0; i < wireVariants.length; i += 1) {
    const wireType = wireVariants[i];
    try {
      const response = await apiClient.get<PcfSettingsDto>('/pcf_settings', {
        timeout: 10000,
        params: {
          name: normalizedName,
          settingsType: wireType,
        },
      });
      const normalizedResponse: PcfSettingsDto = {
        ...response.data,
        settingsType: normalizeSettingsType(response.data?.settingsType, normalizedSettingsType),
      };
      const summary = summarizePcfSettingsDtoForDebug(normalizedResponse);
      webLog.info(
        'pcfSettingsClient',
        `getPipeexSettings: remote load succeeded name=${normalizedName} settingsType=${normalizedSettingsType} summary=${JSON.stringify(summary)}`
      );
      debugPcfSettings('[pcfSettingsClient] getPipeexSettings API success', {
        profileKey: getProfileKey(normalizedName, normalizedSettingsType),
        wireType,
        summary,
        dataFlow: summarizePcfSettingsDtoDataFlowForDebug(normalizedResponse),
      });
      return normalizedResponse;
    } catch (error) {
      lastError = error;
      if (i < wireVariants.length - 1 && isRetryableSettingsTypeContractError(error)) {
        continue;
      }
      break;
    }
  }

  if (isAuthFailureError(lastError)) {
    throw lastError ?? new Error('Authentication failed while loading settings profile');
  }
  throw lastError ?? new Error('Failed to load settings profile');
}

export async function deletePipeexSettings(
  name: string,
  settingsType: SettingsType
): Promise<void> {
  const normalizedSettingsType = normalizeSettingsType(settingsType);
  const normalizedName = normalizeScopedProfileName(name, normalizedSettingsType, DEFAULT_PIPEEX_PROFILE_NAME);
  let lastError: unknown = null;
  const wireVariants = getSettingsTypeWireVariants(normalizedSettingsType);
  for (let i = 0; i < wireVariants.length; i += 1) {
    const wireType = wireVariants[i];
    const encodedName = encodeURIComponent(normalizedName);
    try {
      await apiClient.delete(`/pcf_settings/${encodedName}`, {
        timeout: 10000,
        params: {
          name: normalizedName,
          settingsType: wireType,
        },
      });
      return;
    } catch (error) {
      lastError = error;
      if (i < wireVariants.length - 1 && isRetryableSettingsTypeContractError(error)) {
        continue;
      }
      break;
    }
  }

  if (isAuthFailureError(lastError)) {
    throw lastError ?? new Error('Authentication failed while deleting settings profile');
  }

  throw lastError ?? new Error('Failed to delete settings profile');
}
